import Ezcancel from '@/src/assets/svgs/Ezcancel'
import Flexifly from '@/src/assets/svgs/Flexifly'
import { INTL_CANCELATION_INSURANCE, INTL_FLEXIFLY } from '@/src/constants/itnerary'
import { Fare } from '@/src/types/itinerary'
import { TranslationFunction } from '@/src/types/translations'
import { Verified } from '@mui/icons-material'
import {
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'

interface FareCardsProps {
    fares: Fare[]
    brandName: string
    onFareSelect: (fare: Fare) => void
    formatFareTitle: (title: string) => string
    formatCurrency: (price: number) => string
    t: TranslationFunction<'common'>
    onViewBenefits: (category: string) => void
}

const FareCards: React.FC<FareCardsProps> = ({
    fares,
    brandName,
    onFareSelect,
    formatFareTitle,
    formatCurrency,
    t,
    onViewBenefits
}) => {
    return (
        <Box>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: 'textColors.primary',
                    fontSize: { xs: '16px', md: '1.25rem' }
                }}
            >
                {t('selectYourFare')}
            </Typography>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 1.5,
                            alignItems: 'stretch',
                            '& > *': {
                                flex: { lg: '1 1 0%' },
                                minWidth: 0
                            }
                        }}
                    >
                        {fares.map((fare, index) => {
                            const { price, fareMetaMessages, selected, totalPremiumAmount = 0 } = fare
                            const fareMetaMessage = fareMetaMessages?.[0] || {}
                            const { title, category, messages = [] } = fareMetaMessage || {}
                            const isFlexifly = category === INTL_FLEXIFLY
                            const isEzCancel = category === INTL_CANCELATION_INSURANCE
                            const isRecommended = isFlexifly
                            const isPremium = isEzCancel
                            const showPremium = totalPremiumAmount > 0

                            // Use messages directly from fareMetaMessages
                            // const messages = fareMetaMessage?.messages || []

                            return (
                                <Card
                                    key={index}
                                    sx={{
                                        position: 'relative',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: selected ? 'primary.main' : 'borders.default',
                                        backgroundColor: selected
                                            ? theme => alpha(theme.palette.primary.main, 0.1)
                                            : 'neutral.white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: selected
                                                ? theme => alpha(theme.palette.primary.main, 0.1)
                                                : 'neutral.100'
                                        },
                                        boxShadow: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: 'auto'
                                    }}
                                    onClick={() => onFareSelect(fare)}
                                >
                                    {/* Badge for recommended/premium */}
                                    {(isRecommended || isPremium) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -4,
                                                right: 12,
                                                zIndex: 10
                                            }}
                                        >
                                            <Chip
                                                icon={<Verified sx={{ fontSize: '12px !important' }} />}
                                                label={
                                                    isRecommended
                                                        ? t('recommended')?.toUpperCase() || 'RECOMMENDED'
                                                        : t('premium')?.toUpperCase() || 'PREMIUM'
                                                }
                                                size="small"
                                                sx={{
                                                    bgcolor: isRecommended ? 'secondary.main' : 'primary.main',
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    fontSize: '0.6875rem',
                                                    height: 22,
                                                    borderRadius: '0 0 8px 8px',
                                                    '& .MuiChip-icon': {
                                                        color: 'white',
                                                        fontSize: 12
                                                    },
                                                    '& .MuiChip-label': {
                                                        px: 0.75,
                                                        py: 0.25
                                                    }
                                                }}
                                                clickable={false}
                                                onClick={() => {}}
                                            />
                                        </Box>
                                    )}

                                    <CardContent
                                        sx={{
                                            p: 1.25,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            '&:last-child': { pb: 1.25 }
                                        }}
                                    >
                                        {/* Content that grows */}
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                                                <FormControlLabel
                                                    value={index.toString()}
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                color: 'grey.400',
                                                                '&.Mui-checked': {
                                                                    color: 'primary.main'
                                                                },
                                                                p: 0,
                                                                mr: 0.75
                                                            }}
                                                        />
                                                    }
                                                    label=""
                                                    sx={{ m: 0, alignItems: 'flex-start' }}
                                                    checked={selected}
                                                />

                                                <Box sx={{ flex: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'baseline',
                                                            gap: 1,
                                                            mb: 0.25
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mt: 0.25
                                                            }}
                                                        >
                                                            {(isEzCancel || isFlexifly) && (
                                                                <Box
                                                                    sx={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        borderRadius: '50%',

                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    {isEzCancel ? (
                                                                        <Ezcancel
                                                                            sx={{ fontSize: 20, color: 'white' }}
                                                                        />
                                                                    ) : (
                                                                        <Flexifly
                                                                            sx={{ fontSize: 20, color: 'white' }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            )}
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'textColors.secondary',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.9rem'
                                                                }}
                                                            >
                                                                {formatFareTitle(title)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                                        <Typography
                                                            variant="h4"
                                                            sx={{
                                                                fontWeight: showPremium ? 500 : 700,
                                                                fontSize: showPremium ? '16px' : '22px',
                                                                mb: 0.75,
                                                                lineHeight: 1.2,
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            {formatCurrency(price)}
                                                            {showPremium && (
                                                                <>
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontSize: '22px',
                                                                            mx: 0.3
                                                                        }}
                                                                    >
                                                                        +
                                                                    </Typography>
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            fontSize: '22px'
                                                                        }}
                                                                    >
                                                                        {formatCurrency(totalPremiumAmount)}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Typography>
                                                    </Box>

                                                    {Array.isArray(messages) && (
                                                        <Box>
                                                            {messages.map((message, index) => {
                                                                return (
                                                                    <Typography
                                                                        key={index}
                                                                        variant="body2"
                                                                        component="div"
                                                                        sx={{
                                                                            color: 'textColors.secondary',
                                                                            fontSize: '0.8125rem',
                                                                            lineHeight: 1.5,
                                                                            mb: index < messages.length - 1 ? 0.5 : 0
                                                                        }}
                                                                    >
                                                                        {message}
                                                                        {(isFlexifly || isEzCancel) && (
                                                                            <>
                                                                                {' '}
                                                                                <Typography
                                                                                    component="span"
                                                                                    onClick={(e: React.MouseEvent) => {
                                                                                        e.stopPropagation()
                                                                                        onViewBenefits(category || '')
                                                                                    }}
                                                                                    sx={{
                                                                                        color: 'primary.main',
                                                                                        fontSize: '0.8125rem',
                                                                                        fontWeight: 500,
                                                                                        cursor: 'pointer',
                                                                                        display: { xs: 'inline', md: 'inline', lg: 'none' },
                                                                                        whiteSpace: 'nowrap',
                                                                                        ml: 0.5
                                                                                    }}
                                                                                >
                                                                                    {t('viewBenefits')}
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                    </Typography>
                                                                )
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Footer pinned to bottom */}
                                        {(isFlexifly || isEzCancel) && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center',
                                                    pt: 0.75,
                                                    mt: 0.75
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'textColors.disabled',
                                                        fontSize: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    }}
                                                >
                                                    {t('poweredBy')}
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: '12px',
                                                            color: 'primary.main',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {brandName}
                                                    </Typography>
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </Box>
                </RadioGroup>
            </FormControl>
        </Box>
    )
}

export { FareCards }
export type { FareCardsProps }
